/* ==========================================================================
   assets/pf-finder.js — Poufudic cushion listing page
   --------------------------------------------------------------------------
   Six jobs, and nothing else:
     0. tightening the server's cover-only demotion to the REAL column count.
        The section already demotes past the widest grid it can be (5 columns),
        which is correct at every width without any script at all; this measures
        what the grid actually is right now and re-checks it when the viewport
        changes. It only ever moves a card DOWN, from the order the server
        rendered, and it never hides one.
     1. chip filtering (single-select, client side, over the cards on the page)
     2. keeping the heading's listing count honest while a chip is on — the
        server prints the true unfiltered sentence, this rewrites it to "13 of
        16 listings" and puts the original back on release. Nothing here is
        load-bearing: with no script the count the server printed is already
        the right one, because nothing has been filtered out.
     3. address-bar state, so the back button from a product page restores the
        filter and an ad can link straight to one attribute
     4. the sticky chip row's top offset, measured from whatever the theme's
        header actually is, so the two never overlap
     5. the scroll affordance on the chip row (a fade that hides at the end)

   It creates no element, injects no markup into the grid, and adds no
   listener inside `.pf-fn__grid`. The grid stays 0 buttons / 0 forms /
   0 inputs at runtime as well as at render time. The demotion in job 0
   RE-INSERTS cards that the server already rendered — it never builds one,
   never removes one and never inserts a non-element node, so that guarantee
   holds through it.

   Every chip rendered by the section matches at least one and at most
   (total - 1) products, so a single-select filter can never produce an empty
   grid. There is deliberately no zero-results state, because it is
   unreachable.

   KILL SWITCH: ?pf_finder_off=1
   The attribute this used to set is now written by an inline script at the top
   of the section, before first paint - this file is deferred, so setting it
   here meant the section painted and then changed, which is a flash rather
   than a switch. The check below stays as the belt to that inline brace: it is
   what stops the filter, the sticky offset and the chip row from booting.
   Every card stays on the page either way; with the section owning the grid,
   hiding it would leave this URL with no products on it at all.
   ========================================================================== */
(function () {
  'use strict';

  var PARAM = 'pf_chip';

  function qs(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  if (qs('pf_finder_off') === '1') {
    // Idempotent: the section's inline bootstrap has already set this before
    // first paint. Written again here so the switch still works if the inline
    // script is ever stripped (a CSP without 'unsafe-inline', an optimiser),
    // in which case the CSS applies one frame later instead of never.
    document.documentElement.setAttribute('data-pf-finder-off', '1');
    return;
  }

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* ---------------------------------------------------------------- filter */

  function Finder(root) {
    this.root = root;
    this.chips = root.querySelectorAll('[data-pf-chip]');
    this.cards = root.querySelectorAll('[data-pf-item]');
    this.scroller = root.querySelector('[data-pf-chips]');
    this.useUrl = root.getAttribute('data-pf-urlstate') === '1';

    // The heading's listing count, so it can follow the filter. Captured
    // BEFORE the first apply() so the sentence the server printed is what we
    // put back when the filter is released — we never rebuild it, so the
    // merchant's own wording and number survive verbatim. Absent when the
    // owner has switched the count off in the theme editor; null-guarded
    // everywhere below.
    this.count = root.querySelector('[data-pf-count]');
    this.countBase = this.count ? this.count.textContent : '';
    this.countNoun = this.count ? (this.count.getAttribute('data-pf-noun') || '') : '';

    // Cover-only demotion, client half.
    this.grid = root.querySelector('[data-pf-grid]');
    this.demote = root.getAttribute('data-pf-demote') === '1';
    this.lastCols = 0;

    // BEFORE the filter is applied, so the base order is stamped from the
    // order the server actually rendered rather than from whatever a deep
    // link happens to leave visible.
    this.arrange();
    this.bind();
    this.restore();
    this.overflow();
  }

  /* --------------------------------------------- row-1 cover-only demotion --

     WHY. A cover-only listing is the cheapest product in this collection and
     it contains no foam. In the first row, next to filled cushions at $58.70,
     it wins a price comparison it should never have been entered in — and the
     first row is exactly what an ad click lands on. The section demotes it
     server-side past the widest the grid can ever be, which is already correct
     everywhere; this is the client half, and all it adds is the REAL column
     count at this viewport, re-checked when that count changes.

     THE THREE INVARIANTS, and they are the reason this is written the way it
     is rather than as a sort:
       - nothing is ever moved UP. `head` keeps the non-cover cards that were
         already in row 1, row 1 is refilled from the TOP of what already
         followed it, and the movers are re-inserted immediately after that
         refill — so every card either stays where it was or goes further down.
       - nothing is ever hidden. This only re-inserts existing nodes; it never
         sets `hidden`, never removes a node and never creates one.
       - it is idempotent. Every card is stamped once with the position it was
         RENDERED in (`data-pf-ord`) and every later pass sorts from that same
         base, so rotating a phone cannot walk a card down the grid one row per
         rotation.

     It also inserts nothing but the cards themselves — no marker node, no
     comment node. The theme's own script maps querySelector over nodes added
     to a product grid and throws on a node that is not an element.            */

  /* HOW MANY COLUMNS IS THIS GRID, RIGHT NOW? Not what the CSS says — count
     the cards that share the first visible card's offsetTop. They are all
     children of the same grid, so their offsetTop values are directly
     comparable. Hidden cards (a chip is on) report 0 and are skipped. 2px of
     slack: sub-pixel row heights are normal, a real second row is hundreds of
     pixels down. */
  Finder.prototype.columnCount = function (items) {
    var vis = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].offsetParent !== null || items[i].offsetHeight > 0) vis.push(items[i]);
    }
    if (vis.length < 2) return vis.length ? 1 : 0;
    var top0 = vis[0].offsetTop;
    var n = 0;
    for (var j = 0; j < vis.length; j++) {
      if (Math.abs(vis[j].offsetTop - top0) <= 2) n++;
      else break;
    }
    return n || 1;
  };

  Finder.prototype.arrange = function () {
    if (!this.demote || !this.grid) return;

    // Live, in DOM order — `this.cards` is a static list captured once and is
    // deliberately NOT used here, because this function reorders the DOM.
    var items = Array.prototype.slice.call(this.grid.querySelectorAll('[data-pf-item]'));
    if (items.length < 2) return;

    for (var s = 0; s < items.length; s++) {
      if (!items[s].hasAttribute('data-pf-ord')) items[s].setAttribute('data-pf-ord', String(s));
    }

    var cols = this.columnCount(items);
    if (cols < 1) return;                 // nothing laid out yet
    this.lastCols = cols;
    if (cols >= items.length) return;     // one row is the whole grid

    var base = items.slice().sort(function (a, b) {
      return (parseInt(a.getAttribute('data-pf-ord'), 10) || 0) -
             (parseInt(b.getAttribute('data-pf-ord'), 10) || 0);
    });

    // The same `cover` key the chip row filters on and the section's attribute
    // ladder derived — one classification, server-side, read here.
    function isCover(el) {
      return (' ' + (el.getAttribute('data-pf-keys') || '') + ' ').indexOf(' cover ') !== -1;
    }

    var head = [];
    var movers = [];
    for (var i = 0; i < cols; i++) {
      if (isCover(base[i])) movers.push(base[i]);
      else head.push(base[i]);
    }
    if (!movers.length) return;

    var rest = base.slice(cols);
    var need = cols - head.length;        // refill row 1 from the top of the rest
    var desired = head.concat(rest.slice(0, need), movers, rest.slice(need));

    var changed = false;
    for (var k = 0; k < desired.length; k++) {
      if (items[k] !== desired[k]) { changed = true; break; }
    }
    if (!changed) return;

    this.grid.insertBefore(desired[0], items[0]);
    for (var m = 1; m < desired.length; m++) {
      this.grid.insertBefore(desired[m], desired[m - 1].nextSibling);
    }
  };

  Finder.prototype.bind = function () {
    var self = this;
    each(this.chips, function (chip) {
      chip.addEventListener('click', function () {
        var key = chip.getAttribute('data-pf-chip') || '';
        // Tapping the chip that is already on turns it off, which is the
        // same state as "All". There is no other way to reach an empty grid
        // and this one does not reach it either.
        if (chip.getAttribute('aria-pressed') === 'true') key = '';
        self.apply(key, true);
      });
    });

    if (this.scroller) {
      var self2 = this;
      this.scroller.addEventListener('scroll', function () { self2.overflow(); }, { passive: true });
      window.addEventListener('resize', function () { self2.overflow(); }, { passive: true });
    }

    // The column count moves with the viewport (2-up on a phone, 5-up at
    // 1700). Re-run the demotion only when the count ACTUALLY changed, and
    // always from the stamped base order, so a rotation or a resize can never
    // walk a card further down than the server put it.
    if (this.demote) {
      var self3 = this;
      var t = 0;
      window.addEventListener('resize', function () {
        window.clearTimeout(t);
        t = window.setTimeout(function () {
          if (!self3.grid) return;
          var cols = self3.columnCount(
            Array.prototype.slice.call(self3.grid.querySelectorAll('[data-pf-item]'))
          );
          if (cols && cols !== self3.lastCols) self3.arrange();
        }, 180);
      }, { passive: true });
    }

    // The back button from a product page restores the filter, not the page.
    window.addEventListener('popstate', function () {
      self.apply(qs(PARAM) || '', false);
    });
  };

  Finder.prototype.restore = function () {
    var key = this.useUrl ? (qs(PARAM) || '') : '';
    // A key that matches no chip (a stale link, a renamed attribute) falls
    // back to "everything", never to an empty grid.
    var known = false;
    each(this.chips, function (chip) {
      if ((chip.getAttribute('data-pf-chip') || '') === key) known = true;
    });
    this.apply(known ? key : '', false);
  };

  Finder.prototype.apply = function (key, push) {
    var shown = 0;

    each(this.chips, function (chip) {
      var mine = (chip.getAttribute('data-pf-chip') || '') === key;
      chip.setAttribute('aria-pressed', mine ? 'true' : 'false');
    });

    each(this.cards, function (card) {
      var keys = ' ' + (card.getAttribute('data-pf-keys') || '') + ' ';
      var on = key === '' || keys.indexOf(' ' + key + ' ') !== -1;
      if (on) { card.removeAttribute('hidden'); shown++; }
      else { card.setAttribute('hidden', 'hidden'); }
    });

    // Defensive only. By construction `shown` cannot be 0 — the section does
    // not render a chip whose count is 0. If data ever drifts, show
    // everything rather than an empty page.
    if (shown === 0) {
      each(this.cards, function (card) { card.removeAttribute('hidden'); });
      each(this.chips, function (chip) {
        chip.setAttribute('aria-pressed', (chip.getAttribute('data-pf-chip') || '') === '' ? 'true' : 'false');
      });
      key = '';
    }

    // THE COUNT FOLLOWS THE FILTER.
    // "16 listings" over 13 cards was the one thing on this page that
    // contradicted the page. While a chip is on, the heading reports what is
    // actually on screen and what it is out of; released, the server's own
    // sentence goes back verbatim.
    //
    // The denominator is the number of cards ON THIS PAGE, not the collection
    // total, because the chips filter this page and only this page. Today the
    // two are the same number (16 of 16, one page of 48). Past 48 they are
    // not, and "13 of 48 listings" would still be true of what the shopper can
    // reach, where "13 of 60" would not be.
    if (this.count) {
      this.count.textContent = key === ''
        ? this.countBase
        : shown + ' of ' + this.cards.length + ' ' + this.countNoun;
    }

    var live = this.root.querySelector('[data-pf-live]');
    if (live) {
      var label = '';
      each(this.chips, function (chip) {
        if ((chip.getAttribute('data-pf-chip') || '') === key) label = (chip.textContent || '').trim();
      });
      live.textContent = key === ''
        ? ''
        : shown + ' of ' + this.cards.length + ' shown, filtered by ' + label;
    }

    if (push && this.useUrl) this.url(key);
    this.center(key);
  };

  Finder.prototype.center = function (key) {
    if (!this.scroller) return;
    var el = null;
    each(this.chips, function (chip) {
      if ((chip.getAttribute('data-pf-chip') || '') === key) el = chip;
    });
    if (!el || key === '') return;
    var r = el.getBoundingClientRect();
    var s = this.scroller.getBoundingClientRect();
    if (r.left < s.left || r.right > s.right) {
      this.scroller.scrollLeft += (r.left - s.left) - 16;
    }
    this.overflow();
  };

  Finder.prototype.url = function (key) {
    if (!window.history || !window.history.pushState) return;
    try {
      var u = new URL(window.location.href);
      if (key === '') u.searchParams.delete(PARAM);
      else u.searchParams.set(PARAM, key);
      window.history.pushState({ pf: key }, '', u.toString());
    } catch (e) { /* a browser without URL(): the filter still works */ }
  };

  Finder.prototype.overflow = function () {
    var s = this.scroller;
    if (!s) return;
    var over = s.scrollWidth - s.clientWidth > 2;
    this.root.classList.toggle('has-overflow', over);
    this.root.classList.toggle('is-end', over && (s.scrollLeft + s.clientWidth >= s.scrollWidth - 2));
  };

  /* -------------------------------------------------------------- sticky --
     The chip row parks under whatever the theme is actually using as a sticky
     header. The height is measured, never hardcoded — the mobile header and
     the desktop header are different elements with different heights here.

     AND IT IS RE-MEASURED ON EVERY SCROLL FRAME, because this theme's header
     is a hide-on-scroll-down / show-on-scroll-up bar. Measured on the real
     storefront at 390 scrolled to y=1200: the header group sat at top -50px
     (bottom 0), so a one-shot measurement returns 0 — and then the header
     slides back down over the chip row the moment the shopper scrolls up, and
     eats the filter control. One `getBoundingClientRect` per animation frame,
     coalesced, and the custom property is written only when it actually
     changes, so there is no layout thrash.                                  */

  function stickyOffset(root) {
    if (root.getAttribute('data-pf-sticky') !== '1') return;
    root.classList.add('pf-fn--sticky');

    var heads = [];
    var last = -1;
    var queued = false;

    function collect() {
      heads = [];
      each(document.querySelectorAll(
        'header, .header, #header, [class*="header-group"], [class*="header-sticky"], [class*="sticky-header"]'
      ), function (el) {
        var cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        if (root.contains(el)) return;            // never measure ourselves
        heads.push(el);
      });
    }

    function measure() {
      queued = false;
      var top = 0;
      var cap = window.innerHeight * 0.4;         // a "header" taller than this
      each(heads, function (el) {                 // is a drawer, not a header
        var r = el.getBoundingClientRect();
        if (r.height === 0 || r.height > cap) return;
        if (r.top > 4) return;                    // not parked at the top
        if (r.bottom > top) top = r.bottom;
      });
      if (top < 0) top = 0;
      top = Math.round(top);
      if (top === last) return;
      last = top;
      root.style.setProperty('--pf-sticky-top', top + 'px');
    }

    function schedule() {
      if (queued) return;
      queued = true;
      (window.requestAnimationFrame || window.setTimeout)(measure, 16);
    }

    collect();
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', function () { collect(); schedule(); }, { passive: true });
    window.addEventListener('load', function () { collect(); schedule(); });

    var bar = root.querySelector('[data-pf-chipbar]');
    if (bar && 'IntersectionObserver' in window) {
      // A one-pixel sentinel directly above the bar tells us when the bar is
      // stuck, so the hairline under it appears only when it is needed.
      var io = new IntersectionObserver(function (entries) {
        root.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }, { threshold: [1], rootMargin: '-1px 0px 0px 0px' });
      io.observe(bar);
    }
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    each(document.querySelectorAll('[data-pf-finder]'), function (root) {
      if (root.getAttribute('data-pf-ready') === '1') return;
      root.setAttribute('data-pf-ready', '1');
      try { new Finder(root); } catch (e) { /* never take the page down */ }
      try { stickyOffset(root); } catch (e) { /* ditto */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-boot when the section is re-rendered.
  document.addEventListener('shopify:section:load', boot);
})();
